import { financialProtectionMessage, protectedRange } from '../shared/models/financial-protection';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Project } from '../projects/domain/project';
import { distinctUntilChanged, map, shareReplay, skip, switchMap, tap } from 'rxjs/operators';
import { ProjectList } from '../projects/domain/projectList';
import { SearchProjectData } from '../projects/domain/search-project-data';
import { PhaseAndEmployees } from '../projects/domain/phase-and-employees';
import { PhaseWithEmployees } from '../projects/domain/phase-with-employees';
import { ConflictingProject } from '../projects/domain/conflicting-project';
import { EmployeeInProject } from '../projects/domain/employee-in-project';
import { EmployeeConflict } from '../shared/models/employee-conflict';
import { TimeSheet } from "../projects/domain/time-sheet";
import { TaskWithEmployees } from "../projects/domain/task-with-employees";
import { UserService } from '../../core/user.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private projectCache: Map<string, Observable<Project>> = new Map();
  private projectCacheRefreshers: Map<string, BehaviorSubject<void>> = new Map();

  private phasesCache: Map<string, Observable<PhaseWithEmployees[]>> = new Map();
  private phasesCacheRefreshers: Map<string, BehaviorSubject<void>> = new Map();

  private tasksCache: Map<string, Observable<TaskWithEmployees[]>> = new Map();
  private tasksCacheRefreshers: Map<string, BehaviorSubject<void>> = new Map();

  private recommendedPhasesCache = new Map<string, Observable<PhaseWithEmployees[]>>();
  private recommendedPhasesCacheRefreshers = new Map<string, BehaviorSubject<void>>();

  constructor(private httpClient: HttpClient, private userService: UserService) {
    this.userService.user$.pipe(
      map(user => user?.userId ?? null),
      distinctUntilChanged(),
      skip(1),
    ).subscribe(() => this.clearCache());
  }

  private clearCache(): void {
    this.projectCache.clear();
    this.projectCacheRefreshers.clear();
    this.phasesCache.clear();
    this.phasesCacheRefreshers.clear();
    this.tasksCache.clear();
    this.tasksCacheRefreshers.clear();
    this.recommendedPhasesCache.clear();
    this.recommendedPhasesCacheRefreshers.clear();
  }

  getProject(projectId: string): Observable<Project> {
    if (!this.projectCache.has(projectId)) {
      this.projectCacheRefreshers.set(projectId, new BehaviorSubject<void>(undefined));

      this.projectCache.set(projectId,
        this.projectCacheRefreshers
          .get(projectId)
          .pipe(
            switchMap(() => this.httpClient.get<Project>(`${ environment.api }/project/${ projectId }`)),
            shareReplay(1),
          ),
      );
    }

    return this.projectCache.get(projectId);
  }

  refreshProject(projectId: string, phasesOnly = false): void {
    if (phasesOnly) {
      this.phasesCacheRefreshers.get(projectId)?.next();
      return;
    }
    this.projectCacheRefreshers.get(projectId)?.next();
    this.phasesCacheRefreshers.get(projectId)?.next();
    this.tasksCacheRefreshers.get(projectId)?.next();
    this.recommendedPhasesCacheRefreshers.get(projectId)?.next();
  }

  getProjectPhasesWithEmployees(projectId: string, fresh = false): Observable<PhaseWithEmployees[]> {
    if (fresh) {
      return this.httpClient.get<PhaseWithEmployees[]>(`${ environment.api }/project/${ projectId }/phase-employee`);
    }
    if (!this.phasesCache.has(projectId)) {
      this.phasesCacheRefreshers.set(projectId, new BehaviorSubject<void>(undefined));

      this.phasesCache.set(projectId,
        this.phasesCacheRefreshers
          .get(projectId)
          .pipe(
            switchMap(() => this.httpClient.get<PhaseWithEmployees[]>(`${ environment.api }/project/${ projectId }/phase-employee`)),
            shareReplay(1),
          ),
      );
    }

    return this.phasesCache.get(projectId);
  }

  getProjectTasksListWithEmployees(projectId: string): Observable<TaskWithEmployees[]> {
    if (!this.tasksCache.has(projectId)) {
      this.tasksCacheRefreshers.set(projectId, new BehaviorSubject<void>(undefined));

      this.tasksCache.set(
        projectId,
        this.tasksCacheRefreshers
          .get(projectId)
          .pipe(
            switchMap(() =>
              this.httpClient.get<TaskWithEmployees[]>(
                `${environment.api}/project/${projectId}/project-tasks-list`
              )
            ),
            shareReplay(1)
          )
      );
    }

    return this.tasksCache.get(projectId);
  }

  getProjectPhasesWithRecommendedEmployees(projectId: string): Observable<PhaseWithEmployees[]> {
    if (!this.recommendedPhasesCache.has(projectId)) {
      this.recommendedPhasesCacheRefreshers.set(projectId, new BehaviorSubject<void>(undefined));

      this.recommendedPhasesCache.set(projectId,
        this.recommendedPhasesCacheRefreshers
          .get(projectId)
          .pipe(
            switchMap(() => this.httpClient.get<PhaseWithEmployees[]>(`${ environment.api }/project/${ projectId }/phase-recommended-employee`)),
            shareReplay(1),
          )
      );
    }

    return this.recommendedPhasesCache.get(projectId);
  }

  getProjectPhaseWithEmployees(projectId: string, phaseId: string): Observable<PhaseWithEmployees> {
    return this.getProjectPhasesWithEmployees(projectId).pipe(
      map((phases) => phases.find((phase) => phase.id === phaseId)),
    );
  }

  getProjectPhasesWithEmployeesTimeSheet(
    projectId: string,
    phasesIds: string[],
    startDate?: string|null,
    endDate?: string|null,
    mergedEmployees = false,
  ): Observable<TimeSheet> {
    let httpParams = new HttpParams();

    phasesIds.forEach((phaseId) => {
      httpParams = httpParams.append('phasesIds[]', phaseId);
    });

    if (startDate) {
      httpParams = httpParams.set('from', startDate);
    }

    if (endDate) {
      httpParams = httpParams.set('to', endDate);
    }

    if (mergedEmployees) {
      httpParams = httpParams.set('mergedEmployees', 'true');
    }

    return this.httpClient.get<TimeSheet>(`${environment.api}/project/${projectId}/time-sheet`,
      { params: httpParams }).pipe(
      map((timeSheet) => ({
        ...timeSheet,
        timeSheetRows: (timeSheet.timeSheetRows ?? []).map((row) => ({
          ...row,
          employee: {
            ...row.employee,
            timeSheetGaps: row.employee?.timeSheetGaps
              ? {
                  ...row.employee.timeSheetGaps,
                  emptyDates: row.employee.timeSheetGaps.emptyDates ?? [],
                }
              : null,
          },
        })),
      }))
    );
  }

  createProject(project: Project): Observable<Project> {
    const value = Object({ ...project });
    return this.httpClient.post<Project>(`${ environment.api }/project`, value);
  }

  editProject(project: Project): Observable<Project> {
    const value = Object({ ...project });
    return this.httpClient.put<Project>(`${ environment.api }/project/${ project.id }`, value);
  }

  getProjectList(data: Partial<SearchProjectData>): Observable<ProjectList> {
    let searchString = '';
    searchString += data.search ? `&search=${ data.search }` : '';
    searchString += data.clientId ? `&clientId=${ data.clientId }` : '';
    searchString += data.vesselId ? `&vesselId=${ data.vesselId }` : '';
    searchString += data.consultantId ? `&consultantId=${ data.consultantId }` : '';
    searchString += data.statusId ? `&statusId=${ data.statusId }` : '';
    searchString += data.managerId ? `&managerId=${ data.managerId }` : '';
    searchString += data.offshoreMarine ? `&offshoreMarine=${ data.offshoreMarine }` : '';
    searchString += data.projectScope ? `&projectScope=${ data.projectScope }` : '';
    searchString += data.active ? `&sort=${data.active}` : '';
    searchString += data.direction ? `&order=${data.direction}` : '';


    return this.httpClient.get<ProjectList>(
      `${ environment.api }/project?limit=${ data.pageSize }&page=${ data.pageIndex + 1 }${ searchString }`,
    );
  }

  addPhase(phase: PhaseAndEmployees, projectId: string): Observable<string> {
    return this.httpClient.post<string>(`${ environment.api }/project/${ projectId }/phase`, phase);
  }

  editPhase(phase: PhaseAndEmployees, projectId: string): Observable<string> {
    return this.httpClient.put<string>(`${ environment.api }/project/${ projectId }/phase/${ phase.id }`, phase);
  }

  editPhaseWithEmployees(phase: PhaseAndEmployees, projectId: string, employeesToEdit: string[]) {
    return this.httpClient.put<string>(`${ environment.api }/project/${ projectId }/phase/${ phase.id }/with-employees`,
      { ...phase, employeeEntryIds: employeesToEdit });
  }

  updateProjectStatus(projectId, statusId): Observable<{}> {
    return this.httpClient.put<{}>(`${ environment.api }/project/${ projectId }/status`, { statusId });
  }

  getConflictingProjectsList(projectId: string, employeeOnPhaseId: string): Observable<ConflictingProject[]> {
    return this.httpClient.get<ConflictingProject[]>(
      `${ environment.api }/project/${ projectId }/employee-in-phase/${ employeeOnPhaseId }/conflict-projects`,
    );
  }

  checkConflictWithNewPhaseDates(projectId: string, phaseId: string, startDate: string, endDate: string): Observable<EmployeeConflict[]> {
    return this.httpClient.get<EmployeeConflict[]>(
      `${ environment.api }/project/${ projectId }/phase/${ phaseId }/conflict/${ startDate }/${ endDate }`,
    );
  }

  getEmployeeInProjectList(projectId: string): Observable<EmployeeInProject[]> {
    return this.httpClient.get<EmployeeInProject[]>(
      `${ environment.api }/project/${ projectId }/employee`,
    );
  }

  verifyFinancialRemoval(projectId: string, phaseId?: string, employeeIds?: string[], assignmentIds?: string[]): Observable<PhaseWithEmployees[]> {
    return this.getProjectPhasesWithEmployees(projectId, true).pipe(tap(phases => {
      if (phaseId && !phases.some(phase => phase.id === phaseId)) throw new Error(financialProtectionMessage(undefined));
      const employees = phases.filter(phase => !phaseId || phase.id === phaseId).flatMap(phase => phase.employees);
      if (assignmentIds?.some(id => !employees.some(employee => employee.id === id)) || employeeIds?.some(id => !employees.some(employee => employee.employeeId === id))) throw new Error(financialProtectionMessage(undefined));
      for (const phase of phases.filter(item => !phaseId || item.id === phaseId)) {
        for (const employee of phase.employees.filter(item => (!employeeIds || employeeIds.includes(item.employeeId)) && (!assignmentIds || assignmentIds.includes(item.id)))) {
          const explicit = employee.startWork && employee.endWork;
          if (protectedRange(employee.financialProtection, explicit ? employee.startWork : phase.startDate, explicit ? employee.endWork : phase.endDate)) {
            throw new Error(financialProtectionMessage(employee.financialProtection, true, period => {
              const name = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'This employee';
              if ((employeeIds?.length || assignmentIds?.length || 0) > 1) return 'The selected employees can’t all be removed. Some assignments include protected financial records. Exclude those employees, or contact Finance to arrange the correction.';
              if (assignmentIds) return `${name} can’t be removed from this phase. The assignment includes financial records protected for ${period}. Contact Finance if the assignment needs correcting.`;
              if (employeeIds) return `${name} can’t be removed from this project. Their assignments include financial records protected for ${period}. Contact Finance to review the required change.`;
              if (phaseId) return `This phase can’t be deleted. It contains employees’ financial records protected for ${period}. Contact Finance to review these records before deleting the phase.`;
              return 'The selected projects can’t be deleted. At least one contains financial records protected by a financial report. Review the affected project with Finance before deleting it.';
            }));
          }
        }
      }
    }));
  }

  removeEmployeeFromProject(employeeIds: string[], projectId: string) {
    return this.verifyFinancialRemoval(projectId, undefined, employeeIds).pipe(switchMap(() =>
      this.httpClient.request('delete', `${ environment.api }/project/${ projectId }/employee`, { body: { employeeIds } })));
  }

  removeProjects(projectIds: string[]): Observable<{}> {
    return forkJoin(projectIds.map(id => this.verifyFinancialRemoval(id))).pipe(switchMap(() =>
      this.httpClient.request('delete', `${ environment.api }/project`, { body: { projectIds } })));
  }

  removePhaseFromProject(projectId: string, phaseId: string): Observable<{}> {
    return this.verifyFinancialRemoval(projectId, phaseId).pipe(switchMap(() =>
      this.httpClient.delete(`${ environment.api }/project/${ projectId }/phase/${ phaseId }`)));
  }

  updateProjectTasksList(projectId: string, tasks: number[]): Observable<{}> {
    return this.httpClient.put(`${ environment.api }/project/${ projectId }/tasks`, { tasks });
  }

  updateProjectTasksListWithEmployees(projectId: string, tasks: TaskWithEmployees[]): Observable<{}> {
    return this.httpClient.put(`${ environment.api }/project/${ projectId }/employee-tasks`, { tasks });
  }

  confirmRecommendation(projectId: string, phaseId: string): Observable<{}> {
    return this.httpClient.put(`${ environment.api }/project/${ projectId }/phase/${ phaseId }/recommendation/employee/confirm`, {});
  }

  sendTimeSheetTemplateToManager(projectId: string, year: number, month: number): Observable<void> {
    return this.httpClient.post<void>(
      `${environment.api}/project/${projectId}/time-sheet/send-template/${year}/${month}`,
      {}
    );
  }
}
